import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { Hidden } from '@material-ui/core';
import Banner from '@cpg-its/ads.containers.banner';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { QuerySuccessMessage } from '../../../../../global/components/messages/query-success-message';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { ArcRoutes } from '../../../constants/routes';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { EmploymentView } from '../../compensation/individual-compensation/employer-view';
import { EmploymentState, PositionTypes } from '../employment-model';
import { setUpdateStipPositionStatus, setUnpaidLeaveStatus, setUpdateNonStipPositionStatus } from '../employment-slice';
import { PositionsDisplay } from './positions-display';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { useLockedPageState } from '../../../../../global/hooks/useLockedPageState';
import { LockedPageKeys } from '../../../../../global/models/locked-page-model';
import { LockedPageMessage } from '../../../../../global/components/locked-page-message';
import { isUnpaidLeave } from '../employment-selector';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { getGpsType } from '../../../../../global/helpers/gps-helper';

export function EmploymentLanding() {

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setUnpaidLeaveStatus(ApiStatuses.None));
        dispatch(setUpdateStipPositionStatus(ApiStatuses.None));
        dispatch(setUpdateNonStipPositionStatus(ApiStatuses.None));
    }, []);

    const routeMatch = useRouteMatch();
    const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
    const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
    const buttonClasses = buttonStyles();

    const lockedState = useLockedPageState(LockedPageKeys.Employment);

    const unpaidLeave = isUnpaidLeave(employmentState?.currentEmployment);
    const gpsItem = getGpsType(null, null);


    const { epiContent } = epiProps?.employmentSettingsState;

    const unpaidLeavePath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.UnpaidLeaveOfAbsence}`);
    const stipNewHirePath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.StipNewHire}`);
    return (
        <>
            <LockedPageMessage lockedState={lockedState} />
            <PageHeader
                headerText={epiContent?.employmentLandingTitle}
                description={epiContent?.employmentLandingDescription}
            />
            <Hidden mdUp>
                <SideBarContent>
                    <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                </SideBarContent>
            </Hidden>
            {
                unpaidLeave ?
                    <ContentSpacer>
                        <Banner
                            banner={{
                                type: 'warning',
                                description: <WysiwygWithReactLink content={epiContent.unpaidLeaveAlertMessages} />,
                                label: <p className='input-label'>{epiContent?.unpaidLeaveAlerTitle}</p>,
                            }}
                        />
                    </ContentSpacer>
                    : null
            }
            <DividerSection sectionText={epiContent?.currentEmploymentHeader}>
                <EmploymentView
                    employmentData={employmentState?.currentEmployment || employmentState?.nonStipendiaryEmployment}
                    employerLabel={epiProps?.employmentSettingsState?.epiContent?.employerLabel}
                    clientNumberLabel={epiProps?.compensationSettingsState?.epiContent?.clientNumberLabel}
                    hireDateLabel={epiProps?.employmentSettingsState?.epiContent?.hireDateLabel}
                />
                {!unpaidLeave && !lockedState.isLocked && employmentState.currentEmployment ? <Link className={buttonClasses.secondaryButton} to={unpaidLeavePath}>{epiContent?.addUnpaidButtonText}</Link> : null}
            </DividerSection>
            <DividerSection sectionText={epiContent?.stipendiaryPositionHeader} sectionDescription={epiContent?.stipendiaryPositionDescription}>
                <ContentSpacer>
                    {
                        employmentState.currentEmployment ?
                            <Link
                                className={`${buttonClasses.secondaryButton} ${lockedState.isLocked ? 'disabled' : ''}`}
                                to={{
                                    pathname: `${routeMatch.path}${ArcRoutes.Position}`,
                                    state: {
                                        positionType: PositionTypes.Stipendiary,
                                    },
                                }}>{epiContent?.updateStipendiaryPostionButtonText}</Link> :
                            <Link
                                className={`${buttonClasses.secondaryButton}`}
                                to={stipNewHirePath}>{epiContent?.addStipendiaryEmploymentButtonText || 'Add Stipendiary Employment'}</Link>
                    }
                </ContentSpacer>
                {
                    employmentState.currentEmployment &&
                    <>
                        <QuerySuccessMessage
                            successfulAction={FormTypes.STIP_POSITION}
                            supplement={<WysiwygWithReactLink content={epiContent?.stipPositionSuccessSupplement} />} />
                        <PositionsDisplay
                            employmentData={employmentState.currentEmployment}
                            emptyMessage={epiContent?.noStipendiaryPositionsMessage}
                            isStip
                            editDisabled={lockedState.isLocked} />
                    </>
                }
            </DividerSection>

            <DividerSection sectionText={epiContent?.nonStipendiaryPositionHeader} sectionDescription={epiContent?.noStipendiaryPositionDescription}>
                <ContentSpacer>
                    <Link
                        to={{
                            pathname: `${routeMatch.path}${ArcRoutes.Position}`,
                            state: {
                                positionType: PositionTypes.NonStipendiary,
                            },
                        }} className={`${buttonClasses.secondaryButton} ${lockedState.isLocked ? 'disabled' : ''}`}>{epiContent?.addNonStipendiaryButtonText}</Link>
                </ContentSpacer>
                <QuerySuccessMessage successfulAction={FormTypes.NON_STIP_POSITION} />
                <PositionsDisplay
                    editDisabled={lockedState.isLocked}
                    employmentData={employmentState.nonStipendiaryEmployment}
                    emptyMessage={epiContent?.noNonStipendiaryPositionsMessage} />
            </DividerSection>
        </>
    );
}